import { playChime } from '@/lib/chime';
// Attention orchestration for Customer Assist live sessions. Solves the
// AFK problem: when the agent (or client) is on another tab and the
// other side joins or sends a message, we want them to know — loudly.
//
// Layered notification strategy (all of these fire concurrently so the
// most reliable one wins for whatever environment the user is in):
//
//   1. **In-dialog banner** — drives the `joinedBannerVisible` flag.
//      The dialog renders a big emerald drop-in banner when this is
//      true; the user dismisses it explicitly. Most visible signal
//      when the user IS looking at the tab.
//   2. **Toast** (sonner) — fires on every join. Useful when the user
//      is on the tab but the dialog isn't in their immediate field of
//      view (e.g. they navigated away and came back).
//   3. **Tab title badge** — when tab is hidden, we prefix the
//      original title with "(N) " (new messages) or
//      "✅ Client joined — " (join). Restored to the original title
//      the moment the tab regains focus, and on unmount. This is the
//      universal signal — works in every browser, no permission
//      needed.
//   4. **Desktop notification** (Web Notification API) — the
//      heaviest hammer. Requires explicit permission grant. Once
//      granted, fires on join + on new messages received while the
//      tab is hidden. Uses `requireInteraction: true` on the join
//      notification so it stays put until the agent dismisses it
//      (instead of flashing for 5 seconds and disappearing).
//
// All of these can be wired with `enabled: false` (e.g. before live
// phase) — the hook is a no-op in that state.

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { AssistChatMessage, AssistRole } from './use-assist-channel';

/** What the browser currently allows. `unsupported` covers SSR + a
 *  small handful of browsers without the Notification API. */
export type NotificationState = NotificationPermission | 'unsupported';

export interface UseAssistAttentionResult {
  permission: NotificationState;
  /** True from the moment the other side joins → user dismisses. The
   *  hook does NOT auto-dismiss — being prominently in-your-face is
   *  the point of this banner. */
  joinedBannerVisible: boolean;
  dismissJoinedBanner: () => void;
  /** Request desktop notification permission. Must be called from a
   *  user-gesture handler (click) — browsers ignore the prompt
   *  otherwise. Updates `permission` once the user responds. */
  enableNotifications: () => Promise<void>;
  /** Unread count of messages from the OTHER side that arrived while
   *  the tab was hidden. Drives the tab-title badge + the in-dialog
   *  "N new" pill if a renderer wants one. Resets on focus. */
  unreadCount: number;
}

function originalTitle(): string {
  if (typeof document === 'undefined') return '';
  return document.title;
}

export function useAssistAttention(args: {
  /** Whether the OTHER side is currently present in the channel. The
   *  hook fires the join attention sequence on the false → true
   *  transition. */
  otherSideOnline: boolean;
  /** Full message list. The hook scans for new messages from the
   *  other side that arrived while the tab was hidden (or while the
   *  agent's widget is minimized — see `treatAsBackground`). */
  messages: AssistChatMessage[];
  /** The viewer's role. The "other side" is whichever isn't this. */
  viewerRole: AssistRole;
  /** When false, the hook does nothing — no listeners, no title
   *  mutations, no notifications. Used to gate against pre-live
   *  phases. */
  enabled: boolean;
  /** Force the hook to treat the chat as "not being looked at" even
   *  when the tab is focused. Used by the floating widget: while the
   *  widget is minimized to a pill the chat surface isn't visible,
   *  so a new message should fire a desktop notification even though
   *  document.hidden = false. Tab-title flash stays gated on
   *  document.hidden only (no point flashing the title of a page
   *  you're currently looking at). */
  treatAsBackground?: boolean;
}): UseAssistAttentionResult {
  const { otherSideOnline, messages, viewerRole, enabled } = args;
  const treatAsBackground = args.treatAsBackground === true;
  const otherRole: AssistRole = viewerRole === 'agent' ? 'client' : 'agent';
  const otherLabel = otherRole === 'agent' ? 'Agent' : 'Client';

  const [permission, setPermission] = useState<NotificationState>(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return (Notification as unknown as { permission: NotificationPermission }).permission;
  });

  // Keep `permission` in sync with the live browser state. Without this
  // the hook caches whatever Notification.permission was at MOUNT, and
  // if the user grants permission later via the Settings → Desktop
  // notifications card (or via the browser's address-bar prompt), this
  // hook keeps thinking permission is still 'default' and silently
  // skips spawning the join banner.
  //
  // Two channels:
  //   1. Permissions API change event — Chrome / Edge / Firefox fire
  //      `change` whenever the user grants/denies/resets a permission.
  //   2. visibilitychange + focus — Safari historically doesn't expose
  //      the Permissions API for notifications, so we re-check on
  //      tab return as a fallback (cheap; Notification.permission is
  //      a sync getter).
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;

    let cancelled = false;
    let permStatus: PermissionStatus | undefined;
    const refresh = () => {
      if (cancelled) return;
      const next = (Notification as unknown as { permission: NotificationPermission }).permission;
      setPermission((cur) => (cur === next ? cur : next));
    };

    if ('permissions' in navigator && navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          if (cancelled) return;
          permStatus = status;
          refresh();
          status.onchange = refresh;
        })
        .catch(() => {
          // Some browsers throw for "notifications" — silent OK,
          // the focus/visibility listeners still cover it.
        });
    }
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      cancelled = true;
      if (permStatus) permStatus.onchange = null;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  // Helper: read permission LIVE at firing time. Belt-and-suspenders
  // against the cached `permission` state being stale by a few ms
  // (e.g. permission granted on this tab while the hook hasn't
  // received the Permissions API change yet). Notification.permission
  // is a synchronous getter, so the cost is trivial.
  const hasNotificationPermission = (): boolean => {
    if (typeof window === 'undefined' || !('Notification' in window)) return false;
    return (Notification as unknown as { permission: NotificationPermission }).permission === 'granted';
  };

  const [tabVisible, setTabVisible] = useState<boolean>(() => {
    if (typeof document === 'undefined') return true;
    return !document.hidden;
  });

  const [joinedBannerVisible, setJoinedBannerVisible] = useState(false);

  // Refs let us read the latest values inside our long-lived effects
  // without including everything in the deps list (and rebuilding the
  // effect on every render).
  const prevOtherOnline = useRef(false);
  const lastSeenIdRef = useRef<string | null>(null);
  const baseTitleRef = useRef<string>(originalTitle());

  // Track visibility — fires whenever the tab is focused/backgrounded.
  useEffect(() => {
    if (!enabled) return;
    const onChange = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, [enabled]);

  // Derived: messages from the other side, oldest-first (matches the
  // chat scroller order).
  const otherMessages = messages.filter((m) => m.from === otherRole);
  const latestOther = otherMessages[otherMessages.length - 1] ?? null;

  // Unread = messages newer than the lastSeen pointer. If lastSeen is
  // null we count "all" — but we only do that BEFORE the user has ever
  // seen any messages, so it's never inflated past the message count.
  const unreadCount = (() => {
    if (!latestOther) return 0;
    const lastSeen = lastSeenIdRef.current;
    if (!lastSeen) return otherMessages.length;
    const idx = otherMessages.findIndex((m) => m.id === lastSeen);
    if (idx === -1) return otherMessages.length;
    return otherMessages.length - 1 - idx;
  })();

  // Detect "other side just joined".
  useEffect(() => {
    if (!enabled) return;
    const wasOnline = prevOtherOnline.current;
    prevOtherOnline.current = otherSideOnline;
    if (wasOnline || !otherSideOnline) return;

    // Just transitioned offline → online. Run the full attention sequence.
    setJoinedBannerVisible(true);

    toast.success(`${otherLabel} just joined the session`, { duration: 6000 });

    const granted = hasNotificationPermission();
    // Diagnostic console log so a user reporting "no banner fired"
    // can paste DevTools console output and we can immediately see
    // which of the gates (permission state, Notification constructor,
    // OS suppression) was the culprit.
    // eslint-disable-next-line no-console
    console.info('[assist-attention] join detected', {
      otherLabel,
      permissionLive: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
      granted,
    });
    if (granted) {
      try {
        const n = new Notification(`${otherLabel} joined — Customer Assist`, {
          body: 'They are ready in the live session.',
          icon: '/pwa-192.png',
          tag: 'assist-joined',
          requireInteraction: true,
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      } catch (err) {
        // Permission can be revoked between our check + the Notification()
        // call. Surface the cause to DevTools instead of silently failing.
        // eslint-disable-next-line no-console
        console.warn('[assist-attention] Notification() threw on join', err);
      }
    }

    if (typeof document !== 'undefined' && document.hidden) {
      document.title = `${otherLabel} joined — ${baseTitleRef.current}`;
    }
    // We deliberately don't include otherSideOnline as a "value to
    // compute" in the effect; the .current ref + the early return
    // gives us edge-trigger semantics.
  }, [otherSideOnline, enabled, permission, otherLabel]);

  // "Actively viewing chat" = tab focused AND not in background-treated
  // state (e.g. the widget isn't minimized). When this is true, new
  // messages are auto-read; when false, we buzz the user.
  const isActivelyViewing = tabVisible && !treatAsBackground;

  // New messages → desktop notification + title badge (when actually
  // hidden) / no-op (when actively viewing). The treatAsBackground
  // flag lets the floating widget say "I'm minimized — still notify
  // me even though the tab is focused".
  useEffect(() => {
    if (!enabled) return;
    if (!latestOther) return;
    if (lastSeenIdRef.current === latestOther.id) return;

    if (isActivelyViewing) {
      lastSeenIdRef.current = latestOther.id;
      document.title = baseTitleRef.current;
      return;
    }

    // Title flash only when the TAB is hidden — flashing the title of
    // a page you're currently looking at is pointless. The pill on
    // the floating widget carries the visual unread cue for the
    // minimized-but-focused case.
    if (!tabVisible) {
      document.title = `(${unreadCount}) ${baseTitleRef.current}`;
    }

    if (hasNotificationPermission()) {
      try {
        const preview = latestOther.text.slice(0, 120) || '(attachment)';
        const n = new Notification(`${otherLabel} · Customer Assist`, {
          body: preview,
          icon: '/pwa-192.png',
          // Same tag means the previous notification is replaced
          // rather than stacking — desktop OSes can otherwise pile up
          // dozens.
          tag: 'assist-msg',
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      } catch {
        /* swallow */
      }
    }

    // Chat-app-style chime — pairs with the OS banner so the user
    // gets BOTH a visual cue (banner) AND an audible cue (ping) for
    // every new message they couldn't see. Gated on `!isActivelyViewing`
    // (same gate as the OS notification) so we never beep at someone
    // already staring at the chat. The chime is the same two-tone
    // synth used by the in-app desktop-notifications feature
    // (lib/chime.ts) — keeps the sonic vocabulary consistent.
    try {
      playChime();
    } catch {
      /* swallow — autoplay policy etc */
    }
  }, [
    latestOther?.id,
    tabVisible,
    isActivelyViewing,
    enabled,
    permission,
    otherLabel,
    unreadCount,
  ]);

  // Mark read when the agent transitions back to actively viewing
  // (e.g. focuses the tab AND/OR expands the widget). Either signal
  // change qualifies — that's why we depend on `isActivelyViewing`.
  useEffect(() => {
    if (!enabled) return;
    if (!isActivelyViewing) return;
    if (latestOther) lastSeenIdRef.current = latestOther.id;
    document.title = baseTitleRef.current;
  }, [isActivelyViewing, latestOther?.id, enabled]);

  // Restore original title on unmount. Captures via ref so the
  // cleanup function doesn't close over a stale value.
  useEffect(() => {
    if (!enabled) return;
    const t = baseTitleRef.current;
    return () => {
      document.title = t;
    };
  }, [enabled]);

  const enableNotifications = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setPermission('unsupported');
      return;
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch {
      // Older Safari throws on requestPermission instead of returning
      // a rejected promise. Set unsupported so the UI hides the prompt.
      setPermission('unsupported');
    }
  }, []);

  return {
    permission,
    joinedBannerVisible,
    dismissJoinedBanner: useCallback(() => setJoinedBannerVisible(false), []),
    enableNotifications,
    unreadCount,
  };
}
